import { useEffect, type ReactNode } from 'react';
import { Link } from 'react-router';
import { motion, useAnimationControls } from 'motion/react';
import {
  Card,
  Mascot,
  popIn,
  shake,
  spring,
  useReducedMotion,
  type MascotMood,
} from '@redpandev/ui';

/**
 * Lien d'accent des pages auth — contraste AA dans les deux thèmes
 * (panda-400 sur fond sombre, panda-700 sur fond clair).
 */
export const authLink =
  'rounded-sm font-bold text-panda-400 underline-offset-4 hover:underline [[data-theme=light]_&]:text-panda-700';

export type AuthCardProps = {
  /** Titre display de la carte. */
  title: string;
  /** Humeur de la mascotte perchée sur le bord haut de la carte. */
  mood?: MascotMood;
  /** À incrémenter à chaque erreur serveur → secousse douce de la carte. */
  shakeNonce?: number;
  /** Ligne de bas de carte (lien inscription/connexion). */
  footer?: ReactNode;
  children: ReactNode;
};

/**
 * Scène des pages d'auth : fond ambiant discret (blobs), carte centrale
 * translucide (pop-in) et Redpan posée sur le bord haut, qui réagit au
 * formulaire (humeurs animées). Les animations décoratives sont coupées
 * si `prefers-reduced-motion` ; le feedback essentiel reste textuel
 * (`role="alert"` dans les formulaires).
 */
export function AuthCard({
  title,
  mood = 'happy',
  shakeNonce = 0,
  footer,
  children,
}: AuthCardProps) {
  const reduced = useReducedMotion();
  const controls = useAnimationControls();

  // Secousse douce de la carte à chaque nouvelle erreur serveur.
  useEffect(() => {
    if (shakeNonce > 0 && !reduced) void controls.start('shake');
  }, [shakeNonce, reduced, controls]);

  return (
    <main className="relative overflow-hidden px-4 pb-16 pt-20 sm:pt-24">
      {/* Fond ambiant : blobs colorés discrets (purement décoratif). */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0">
        <div className="absolute -left-24 top-6 h-64 w-64 rounded-full bg-panda-500/10 blur-3xl motion-safe:animate-float" />
        <div className="absolute -right-20 top-44 h-72 w-72 rounded-full bg-grape-500/10 blur-3xl motion-safe:animate-float [animation-delay:1.1s]" />
        <div className="absolute -bottom-16 left-1/3 h-56 w-56 rounded-full bg-sky-500/10 blur-3xl motion-safe:animate-float [animation-delay:2.2s]" />
      </div>

      <motion.div
        variants={popIn}
        initial={reduced ? false : 'hidden'}
        animate="visible"
        className="relative mx-auto w-full max-w-md"
      >
        <motion.div variants={shake} animate={controls}>
          {/* Redpan, posée sur le bord haut — décorative : l'info (erreur,
              succès…) est toujours portée par du texte annoncé aux SR. */}
          <div className="pointer-events-none absolute -top-12 left-1/2 z-10 -translate-x-1/2 sm:-top-14">
            <motion.div
              key={mood}
              initial={reduced ? false : { scale: 0.7, opacity: 0, y: 6 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              transition={spring.soft}
            >
              <Mascot mood={mood} size={96} className="h-24 w-24 drop-shadow-lg sm:h-28 sm:w-28" />
            </motion.div>
          </div>

          {/* variant outline + fond translucide inline (token) : évite les
              conflits d'ordre CSS entre utilitaires background/shadow. */}
          <Card
            variant="outline"
            className="px-5 pb-6 pt-16 shadow-glow-panda backdrop-blur-md sm:px-8 sm:pb-8"
            style={{
              backgroundColor: 'rgb(var(--color-surface) / 0.82)',
              borderRadius: 'var(--radius-2xl)',
            }}
          >
            <h1 className="text-center font-display text-3xl font-bold text-ink-100 sm:text-4xl">
              {title}
            </h1>
            <span
              aria-hidden="true"
              className="mx-auto mb-6 mt-2 block h-1 w-12 rounded-full bg-panda-500"
            />
            {children}
            {footer && <p className="mt-6 text-center text-sm text-ink-400">{footer}</p>}
          </Card>
        </motion.div>

        <p className="mt-4 text-center">
          <Link
            to="/"
            className="inline-flex min-h-[2.75rem] items-center gap-1.5 rounded-full px-4 text-sm font-semibold text-ink-400 transition-colors duration-fast ease-fun hover:text-ink-100"
          >
            <span aria-hidden="true">←</span> Retour au hub
          </Link>
        </p>
      </motion.div>
    </main>
  );
}
